import { useEffect, useRef, useState, type ChangeEvent, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { formatDecimal, operationSymbol } from "../lib/format";
import type { WorldTerm } from "../lib/world";
import { HistoryArchive } from "./HistoryArchive";
import { CenterIcon, ChevronIcon, MinusIcon, PlusIcon, ShareIcon, SoundIcon } from "./Icons";

export type SheetStage = 0 | 1 | 2;

interface HistorySheetProps {
  stage: SheetStage;
  terms: readonly WorldTerm[];
  liveTerm: WorldTerm;
  liveTerms: readonly WorldTerm[];
  narrationEnabled: boolean;
  narrationRate: number;
  soundEnabled: boolean;
  onStageChange: (stage: SheetStage) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onCenter: () => void;
  onPanLeft: () => void;
  onPanRight: () => void;
  onPanUp: () => void;
  onPanDown: () => void;
  onNarrationToggle: () => void;
  onNarrationRateChange: (rate: number) => void;
  onSound: () => void;
  onShare: () => void;
}

export function HistorySheet(props: HistorySheetProps) {
  const { onStageChange } = props;
  const toggleRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const historyScrollRef = useRef<HTMLDivElement>(null);
  const [historyOverflow, setHistoryOverflow] = useState(false);
  const isMobile = useMediaQuery("(max-width: 719px)");
  const isOpen = props.stage > 0;

  const close = () => {
    toggleRef.current?.focus();
    onStageChange(0);
  };

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      toggleRef.current?.focus();
      onStageChange(0);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onStageChange]);

  useEffect(() => {
    if (!isOpen) return;
    const scroller = historyScrollRef.current;
    if (!scroller) return;
    const table = scroller.querySelector("table");
    const updateOverflow = () => {
      setHistoryOverflow(scroller.scrollWidth > scroller.clientWidth + 1);
    };

    updateOverflow();
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateOverflow);
      observer.observe(scroller);
      if (table) observer.observe(table);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateOverflow);
    return () => window.removeEventListener("resize", updateOverflow);
  }, [isOpen, props.stage]);

  const onRateChange = (event: ChangeEvent<HTMLSelectElement>) => {
    props.onNarrationRateChange(Number(event.target.value));
  };
  const onHistoryKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    event.currentTarget.scrollLeft += event.key === "ArrowRight" ? 80 : -80;
  };

  return (
    <aside className={`history-sheet history-sheet--${props.stage}`} id="details-sheet" aria-labelledby="details-title">
      <button
        ref={toggleRef}
        className="history-sheet__toggle"
        type="button"
        onClick={() => props.onStageChange(
          isOpen ? (props.stage === 2 && isMobile ? 1 : 0) : 1,
        )}
        aria-expanded={isOpen}
        aria-controls="history-sheet-content"
      >
        <span className="history-sheet__handle" aria-hidden="true" />
        <span id="details-title">
          {props.stage === 2 ? "ALL HISTORY" : <>RECENT TERMS&nbsp; <b>{props.terms.length}</b></>}
        </span>
        <ChevronIcon className={isOpen ? "is-rotated" : ""} />
      </button>

      <div
        ref={contentRef}
        id="history-sheet-content"
        className={props.stage === 2 ? "history-sheet__content history-sheet__content--archive" : "history-sheet__content"}
        hidden={!isOpen}
      >
        {props.stage === 2 && isMobile ? null : <div className="history-sheet__heading">
          <div>
            <p className="micro-label">WORLD 0001</p>
            <h2>DETAILS &amp; HISTORY</h2>
          </div>
          <div className="sheet-actions">
            <button type="button" onClick={() => props.onStageChange(props.stage === 2 ? 1 : 2)}>
              {props.stage === 2 ? "COLLAPSE" : "EXPAND"}
            </button>
            <button type="button" onClick={close}>CLOSE</button>
          </div>
        </div>}

        {props.stage === 1 ? <>
        <section className="sheet-section" aria-labelledby="view-controls-title">
          <h3 id="view-controls-title">VIEW</h3>
          <div className="sheet-control-grid" role="group" aria-label="図の移動と拡大">
            <button type="button" onClick={props.onPanLeft}><span className="text-icon" aria-hidden="true">←</span><span>左へ</span></button>
            <button type="button" onClick={props.onPanUp}><span className="text-icon" aria-hidden="true">↑</span><span>上へ</span></button>
            <button type="button" onClick={props.onPanDown}><span className="text-icon" aria-hidden="true">↓</span><span>下へ</span></button>
            <button type="button" onClick={props.onPanRight}><span className="text-icon" aria-hidden="true">→</span><span>右へ</span></button>
            <button type="button" onClick={props.onZoomOut}><MinusIcon /><span>縮小</span></button>
            <button type="button" onClick={props.onCenter}><CenterIcon /><span>現在地</span></button>
            <button type="button" onClick={props.onZoomIn}><PlusIcon /><span>拡大</span></button>
          </div>
        </section>

        <section className="sheet-section" aria-labelledby="accessibility-title">
          <h3 id="accessibility-title">AUDIO &amp; ACCESSIBILITY</h3>
          <div className="preference-row">
            <button type="button" onClick={props.onSound} aria-label="環境音" aria-pressed={props.soundEnabled}>
              <SoundIcon muted={!props.soundEnabled} />
              <span>環境音 {props.soundEnabled ? "ON" : "OFF"}</span>
            </button>
            <button type="button" onClick={props.onNarrationToggle} aria-label="項の読み上げ" aria-pressed={props.narrationEnabled}>
              <span className="text-icon" aria-hidden="true">Aa</span>
              <span>項の読み上げ {props.narrationEnabled ? "ON" : "OFF"}</span>
            </button>
            <label>
              <span>読み上げ間隔</span>
              <select value={props.narrationRate} onChange={onRateChange} disabled={!props.narrationEnabled}>
                <option value={5}>5項ごと</option>
                <option value={10}>10項ごと</option>
                <option value={30}>30項ごと</option>
              </select>
            </label>
            <button type="button" onClick={props.onShare}><ShareIcon /><span>現在の項を共有</span></button>
          </div>
        </section>

        <section className="sheet-section history-region">
          <div className="section-heading-row">
            <h3 id="history-title">直近20項</h3>
            <span>最新の項から順</span>
          </div>
          <p id="history-scroll-help" className="sr-only">表は横にスクロールできます。矢印キーまたはスワイプで確認してください。</p>
          <div
            ref={historyScrollRef}
            className="history-table-scroll"
            role="region"
            aria-labelledby="history-title"
            aria-describedby={historyOverflow ? "history-scroll-help" : undefined}
            tabIndex={historyOverflow ? 0 : undefined}
            onKeyDown={onHistoryKeyDown}
          >
            <table>
              <caption className="sr-only">直近20項、最新の項から順</caption>
              <thead><tr><th scope="col">項 n</th><th scope="col">前の値</th><th scope="col">規則</th><th scope="col">値 a(n)</th></tr></thead>
              <tbody>
                {props.terms.map((term) => (
                  <tr key={term.n}>
                    <th scope="row">{formatDecimal(term.n)}</th>
                    <td>{formatDecimal(term.previous)}</td>
                    <td className={`is-${term.operation}`}>{operationSymbol(term.operation)} {formatDecimal(term.step)}</td>
                    <td>{formatDecimal(term.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        </> : props.stage === 2 ? (
          <HistoryArchive liveTerm={props.liveTerm} liveTerms={props.liveTerms} isMobile={isMobile} />
        ) : null}
      </div>
    </aside>
  );
}
